import { useSelector } from 'react-redux'
import {
  selectCurrentCountry,
  selectCurrentLanguage,
  selectIsAdobeAnalyticsAvailable,
} from 'redux/selector'
import packageJson from '../../package.json'
import { replaceSpaceByDash } from 'helper'
import {
  onsiteSearchAnalyticsParamsType,
  pageInfoAnalyticsParamsType,
} from 'config/adobeAnalytics/types'
import { useCallback } from 'react'

function isElectron() {
  // Renderer process
  if (
    typeof window !== 'undefined' &&
    typeof window.process === 'object' &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window.process as any).type === 'renderer'
  ) {
    return true
  }

  // Main process
  if (
    typeof process !== 'undefined' &&
    typeof process.versions === 'object' &&
    !!process.versions.electron
  ) {
    return true
  }

  // Detect the user agent when the `nodeIntegration` option is set to false
  if (
    typeof navigator === 'object' &&
    typeof navigator.userAgent === 'string' &&
    navigator.userAgent.indexOf('Electron') >= 0
  ) {
    return true
  }

  return false
}

const useAnalytics = () => {
  const isAdobeAvailable = useSelector(selectIsAdobeAnalyticsAvailable)
  const currentCountry = useSelector(selectCurrentCountry)
  const currentLanguage = useSelector(selectCurrentLanguage)
  const { version } = packageJson

  const initAdobeAnalytics = useCallback(() => {
    if (!isAdobeAvailable) return

    window.digitalData.application.applicationInfo.language = currentLanguage?.languageCode ?? ''
    window.digitalData.application.applicationInfo.country = currentCountry?.name ?? ''
    window.digitalData.application.applicationInfo.version = version

    if (isElectron()) {
      window.digitalData.application.applicationInfo.typo = 'desktop'
    }
  }, [isAdobeAvailable, currentLanguage])

  const setPageInfoAnalytics = ({ pageName, pageId, pageType }: pageInfoAnalyticsParamsType) => {
    if (!isAdobeAvailable) return

    if (pageName) {
      window.digitalData.page.pageInfo.pageName = replaceSpaceByDash(pageName)
    }
    if (pageType) {
      window.digitalData.page.pageInfo.pageType = pageType
    }
    if (pageId) {
      window.digitalData.page.pageInfo.pageId = pageId
    }
  }

  const setApplicationMode = (mode: 'online' | 'offline') => {
    if (!isAdobeAvailable) return

    window.digitalData.application.applicationInfo.mode = mode
  }

  const setSearchAnalytics = ({
    searchTerm,
    searchTotalResult,
  }: onsiteSearchAnalyticsParamsType) => {
    if (!isAdobeAvailable) return

    if (searchTerm) {
      window.digitalData.page.pageInfo.onsiteSearchTerm = searchTerm
    }
    if (searchTotalResult) {
      window.digitalData.page.pageInfo.onsiteSearchResult = searchTotalResult
    }
  }

  return { initAdobeAnalytics, setPageInfoAnalytics, setApplicationMode, setSearchAnalytics }
}

export default useAnalytics
